import {
    Button,
    DialogTrigger,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    makeStyles,
    OverlayDrawer,
    Radio,
    RadioGroup,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, CheckmarkRegular, CopyRegular, DeleteRegular, DismissRegular, Warning20Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSessionStorageState from 'use-session-storage-state';
import { useIsFeatureEnabled } from '../../api/features/features';
import { useGetEntityEfileDataHook, useGetTaxReturnById } from '../../api/report/report';
import { useGetClaimsForCurrentUser } from '../../api/user/user';
import ModalV9 from '../../components/common/ModalV9';
import ConfirmationDialogV9 from '../../components/dialog/ConfirmationDialogV9';
import { useCurrentEnterprise } from '../../contexts/useCurrentEnterprise';
import { useToastMessage } from '../../hooks/useToastMessage';
import { Pillar2AuthType, ReportPillar2Settings } from '../../model';
import type { Pillar2FilingResponse } from '../../model/pillar2FilingResponse';
import type { Report } from '../../model/report';
import type { TestOrganisationRequest } from '../../model/testOrganisationRequest';
import { cometDarkTheme } from '../../theme';
import { PILLAR2_SUBMITTED_REPORT_PREFIX } from '../../utils/BrowserStorageKeys.constants';
import { Pillar2SubmissionEnabled } from '../../utils/FeatureFlags.constants';
import type { FetchError } from '../../utils/UseCustomInstance';
import { useCustomInstance } from '../../utils/UseCustomInstance';
import {
    buildPillar2FilingContext,
    exchangeUkAuthCode,
    executePillar2Filing,
    getPillar2Config,
    getPillar2Id,
    getSamplesAccountingPeriod,
    type Pillar2BffRequest,
} from './executePillar2Filing';
import { fetchPillar2AuthStatus, isFetchUnauthorized, postPillar2AuthLogout } from './pillar2AuthBff';
import { getSubmitUnavailableMessageKey, resolvePillar2FilingMeta } from './pillar2FilingDefaults';
import Pillar2FilingResultModal from './Pillar2FilingResultModal';
import { getJurisdictionBasePath, resolveJurisdiction, usePillar2Metadata } from './pillar2Jurisdictions';
import type { Pillar2DataPreviewStatus } from './shared/Pillar2DataPreviewDialog';
import { Pillar2DataPreviewDialog } from './shared/Pillar2DataPreviewDialog';
import { usePillar2FeatureFlag } from './shared/usePillar2FeatureFlag';
import { usePillar2AuthFlow } from './usePillar2AuthFlow';

export interface IPillar2PanelProps {
    isOpen: boolean;
    onClose: () => void;
    reportId: number;
    pillar2Settings: ReportPillar2Settings;
}

export function getPillar2SubmittedStorageKey(environmentId: string, userId: string, enterpriseId: string, reportId: number): string {
    return `${environmentId}-${userId}-${enterpriseId}_${PILLAR2_SUBMITTED_REPORT_PREFIX}_${reportId}`;
}

type Pillar2ContentModalKind = 'error' | 'warning' | 'info';

type PendingPostAuthAction = 'submit' | 'getObligations' | 'createTestOrg' | 'deleteTestOrg';

interface Pillar2ContentModalState {
    title: string;
    message?: string;
    body?: unknown;
    kind: Pillar2ContentModalKind;
}

/** Let the submit ConfirmationDialog finish fading out before opening the content modal. */
const CONTENT_MODAL_OPEN_DELAY_MS = 200;

const PILLAR2_DEBUG_MODE_QUERY_PARAM = 'p2dm';
const PILLAR2_DEBUG_MODE_QUERY_VALUE = '999';

function isPillar2DebugMode(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    return new URLSearchParams(window.location.search).get(PILLAR2_DEBUG_MODE_QUERY_PARAM) === PILLAR2_DEBUG_MODE_QUERY_VALUE;
}

const usePanelStyles = makeStyles({
    pillar2OverlayDrawer: {
        width: '750px',
        maxWidth: '100vw',
    },
    drawerHeader: {
        padding: '28px 24px 0 24px',
    },
    drawerBody: {
        flex: 1,
        padding: '0 24px',
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
    },
    sidebarTitleStyles: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,
    },
    fieldsStack: {
        marginTop: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    hiddenFileInput: {
        display: 'none',
    },
    debugFileName: {
        alignSelf: 'center',
    },
    fieldRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    fieldLabel: {
        fontWeight: tokens.fontWeightBold,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
    },
    footerSection: {
        flexShrink: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        borderTop: `1px solid ${cometDarkTheme.colorNeutralStroke1}`,
        boxShadow: '0px -5px 5px -5px #00000040',
    },
    actionFooter: {
        flexShrink: 0,
        paddingTop: tokens.spacingVerticalXXS,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'flex-end',
    },
    saveToCloudCard: {
        width: '100%',
        padding: '14px 16px',
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    saveToCloudBlock: {
        width: '100%',
    },
    saveToCloudLabel: {
        display: 'block',
        maxWidth: '100%',
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase400,
        margin: 0,
        marginBottom: '10px',
        color: tokens.colorNeutralForeground2,
    },
    requiredAsterisk: {
        color: tokens.colorPaletteRedForeground1,
    },
    saveToCloudRadioGroup: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: '20px',
        rowGap: tokens.spacingVerticalXXS,
    },
    contentModalMessage: {
        margin: 0,
        marginBottom: tokens.spacingVerticalM,
        maxHeight: '75px',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        width: '710px',
        wordBreak: 'break-word',
    },
    contentModalDetailsLabel: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        marginBottom: tokens.spacingVerticalXS,
    },
    contentModalPre: {
        maxHeight: '360px',
        overflow: 'auto',
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        margin: 0,
        padding: tokens.spacingVerticalM,
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: tokens.colorNeutralBackground3,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    modalActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalS,
    },
    dacInput: {
        width: '100%',
    },
    submitUnavailableMessageContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        width: '100%',
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        textWrap: 'wrap',
    },
    submitUnavailableMessageIcon: {
        alignSelf: 'flex-start',
        marginTop: '2px',
        flexShrink: 0,
    },
});

const formatPillar2SettingValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '_';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return '_';
    }
};

const filingErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
        const fe = err as FetchError;
        const data = fe.response?.data;
        if (typeof data === 'string' && data.trim()) {
            return data;
        }
        if (data && typeof data === 'object' && 'title' in data && typeof (data as { title?: string }).title === 'string') {
            return (data as { title: string }).title;
        }
        if (data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string') {
            return (data as { error: string }).error;
        }
    }
    if (err instanceof Error) {
        return err.message;
    }
    return String(err);
};

function getFileNameFromBlobPath(blobPath: string): string {
    const segment = blobPath.split('/').filter(Boolean).pop();
    return segment?.trim() ? segment : 'pillar2-filing.json';
}

function parseBlobTextForContentModal(text: string): unknown {
    const trimmed = text.trimStart();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }
    return text;
}

function formatPreviewContent(data: unknown): string {
    if (data === null || data === undefined) {
        return '';
    }
    if (typeof data === 'string') {
        const trimmed = data.trimStart();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return JSON.stringify(JSON.parse(data), null, 2);
            } catch {
                return data;
            }
        }
        return data;
    }
    try {
        return JSON.stringify(data, null, 2);
    } catch {
        return String(data);
    }
}

function extractFilingErrorResponseBody(err: unknown): unknown | undefined {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as FetchError).response?.data;
        if (data !== undefined && data !== null) {
            return data;
        }
    }
    return undefined;
}

const Pillar2Panel: React.FC<IPillar2PanelProps> = ({ isOpen, onClose, reportId, pillar2Settings }) => {
    const { t } = useTranslation();
    const panelStyles = usePanelStyles();
    const { showToast } = useToastMessage();
    const { isPillar2Enabled, isPillar2DebugEnabled } = usePillar2FeatureFlag();
    const { metadata } = usePillar2Metadata();
    const bff = useCustomInstance();
    const userClaims = useGetClaimsForCurrentUser();
    const environmentId = userClaims?.data?.environmentId || '';
    const userId = userClaims?.data?.userId || '';
    const { enterprise } = useCurrentEnterprise();
    const { data: taxReturnReport, isLoading: isTaxReturnReportLoading } = useGetTaxReturnById<Report>(reportId);
    const pillar2FilingContext = useMemo(() => buildPillar2FilingContext(enterprise, taxReturnReport), [enterprise, taxReturnReport]);
    const enterpriseId = String(enterprise?.enterpriseId ?? enterprise?.enterpriseKey ?? '');
    const submittedStorageKey = getPillar2SubmittedStorageKey(environmentId, userId, enterpriseId, reportId);
    const [submitted, setSubmitted] = useSessionStorageState<boolean>(submittedStorageKey, { defaultValue: false });

    const getEntityEfileData = useGetEntityEfileDataHook();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewStatus, setPreviewStatus] = useState<Pillar2DataPreviewStatus>('loading');
    const [previewContent, setPreviewContent] = useState<string | undefined>(undefined);
    const [previewErrorDetails, setPreviewErrorDetails] = useState<unknown | undefined>(undefined);

    const [saveToCloudKey, setSaveToCloudKey] = useState('');
    const filingMeta = useMemo(() => resolvePillar2FilingMeta(taxReturnReport, pillar2Settings), [taxReturnReport, pillar2Settings]);
    const jurisdictionId = useMemo(() => resolveJurisdiction(filingMeta.jurisdiction), [filingMeta.jurisdiction]);
    const currentJurisdictionMetadata = useMemo(
        () => (Array.isArray(metadata) ? metadata : []).find((m) => m.id === jurisdictionId),
        [metadata, jurisdictionId]
    );

    const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);
    const [isFiling, setIsFiling] = useState(false);
    // Debug-only (p2dm=999): an uploaded e-file payload to submit instead of the stack-fetched content.
    const [debugUploadedFile, setDebugUploadedFile] = useState<{ name: string; content: string } | null>(null);
    const debugFileInputRef = useRef<HTMLInputElement | null>(null);
    const [isGettingObligations, setIsGettingObligations] = useState(false);
    const [isCreatingTestOrg, setIsCreatingTestOrg] = useState(false);
    const [isDeletingTestOrg, setIsDeletingTestOrg] = useState(false);
    const [isFetchingPillar2Blob, setIsFetchingPillar2Blob] = useState(false);
    const [isFetchingPillar2Record, setIsFetchingPillar2Record] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState<{ authType: Pillar2AuthType; alreadySubmitted: boolean } | null>(null);
    const [contentModal, setContentModal] = useState<Pillar2ContentModalState | null>(null);
    const contentModalTimerRef = useRef<number | null>(null);
    const [filingResultModal, setFilingResultModal] = useState<Pillar2FilingResponse | null>(null);
    const pendingPostAuthActionRef = useRef<PendingPostAuthAction>('submit');
    const runGetObligationsRef = useRef<() => Promise<void>>(async () => {});
    const runCreateTestOrgRef = useRef<() => Promise<void>>(async () => {});
    const runDeleteTestOrgRef = useRef<() => Promise<void>>(async () => {});
    const [showDacModal, setShowDacModal] = useState<boolean>(false);
    const [dac, setDac] = useState<string | null>(null);
    const dacInputRef = useRef<HTMLInputElement>(null);

    const { data: isSubmitFeatureEnabled } = useIsFeatureEnabled({
        featureFlagName: Pillar2SubmissionEnabled,
    });

    const pillar2DebugMode = isPillar2DebugMode() && isPillar2DebugEnabled;

    const canSubmitToJurisdiction = useMemo(() => {
        return (
            taxReturnReport?.pillar2Settings?.allowSubmission &&
            (!taxReturnReport.pillar2Settings.allowSubmissionListCSV ||
                (taxReturnReport.pillar2Settings.efileJurisdiction &&
                    taxReturnReport.pillar2Settings.allowSubmissionListCSV.search(
                        `(^|,)\\s*${taxReturnReport.pillar2Settings.efileJurisdiction}\\s*(,|$)`
                    ) >= 0))
        );
    }, [taxReturnReport]);

    // The submission feature flag is always required. Debug mode (p2dm=999 + Pillar2DebugEnabled) bypasses
    // only the calc-metadata jurisdiction gating so an uploaded payload can be submitted for testing.
    const isSubmitButtonAvailable = isSubmitFeatureEnabled && (pillar2DebugMode || canSubmitToJurisdiction);

    // When submission is enabled but the jurisdiction does not support it, tell the user why Submit is missing.
    // Waits for the report so the message does not flash while the jurisdiction settings are still loading.
    // Hidden in debug mode, where Submit is shown regardless and the audience already knows the jurisdiction gating.
    const showSubmitUnavailableMessage =
        !!isSubmitFeatureEnabled && !pillar2DebugMode && !isTaxReturnReportLoading && !!taxReturnReport && !canSubmitToJurisdiction;

    const contentModalBodyJson = useMemo(() => {
        if (contentModal?.body === undefined) {
            return null;
        }
        if (typeof contentModal.body === 'string') {
            return formatPreviewContent(contentModal.body);
        }
        try {
            return JSON.stringify(contentModal.body, null, 2);
        } catch {
            return String(contentModal.body);
        }
    }, [contentModal]);

    const clearContentModalTimer = useCallback(() => {
        if (contentModalTimerRef.current !== null) {
            window.clearTimeout(contentModalTimerRef.current);
            contentModalTimerRef.current = null;
        }
    }, []);

    const showContentModal = useCallback(
        (next: Pillar2ContentModalState) => {
            clearContentModalTimer();
            contentModalTimerRef.current = window.setTimeout(() => {
                contentModalTimerRef.current = null;
                setContentModal(next);
            }, CONTENT_MODAL_OPEN_DELAY_MS);
        },
        [clearContentModalTimer]
    );

    const dismissContentModal = useCallback(() => {
        clearContentModalTimer();
        setContentModal(null);
    }, [clearContentModalTimer]);

    const handleCopyContentModal = useCallback(async () => {
        if (!contentModal) {
            return;
        }
        const parts: string[] = [];
        if (contentModal.title) {
            parts.push(contentModal.title);
        }
        if (contentModal.message) {
            parts.push(contentModal.message);
        }
        if (contentModalBodyJson) {
            parts.push(contentModalBodyJson);
        }
        const text = parts.join('\n\n');
        try {
            await navigator.clipboard.writeText(text);
            showToast(
                t('pillar2ContentModalCopySuccessTitle').toString(),
                t('pillar2ContentModalCopySuccessDescription').toString(),
                'success'
            );
        } catch {
            showToast(
                t('pillar2ContentModalCopyFailedTitle').toString(),
                t('pillar2ContentModalCopyFailedDescription').toString(),
                'error'
            );
        }
    }, [contentModal, contentModalBodyJson, showToast, t]);

    const openErrorFromThrowable = useCallback(
        (titleKey: string, err: unknown) => {
            const message = filingErrorMessage(err);
            const body = extractFilingErrorResponseBody(err);
            showContentModal({ title: t(titleKey).toString(), message, body, kind: 'error' });
        },
        [showContentModal, t]
    );

    const authFlow = usePillar2AuthFlow({
        fetchAuthorizationUrl: (params) => {
            const basePath = currentJurisdictionMetadata?.basePath ?? getJurisdictionBasePath(jurisdictionId);
            return bff({ url: `${basePath}/auth/url`, method: 'get', params }) as Promise<string>;
        },
        postPillar2AuthLogout: () => {
            const basePath = currentJurisdictionMetadata?.basePath ?? getJurisdictionBasePath(jurisdictionId);
            return bff({ url: `${basePath}/auth/logout`, method: 'post' }) as Promise<void>;
        },
        onAuthCode: async (code, redirectUri) => {
            const pendingAction = pendingPostAuthActionRef.current;
            if (pendingAction === 'getObligations') {
                pendingPostAuthActionRef.current = 'submit';
                try {
                    //TODO: Refactor this and take out UK specific logic
                    await exchangeUkAuthCode(bff as Pillar2BffRequest, code, redirectUri ?? '');
                    await runGetObligationsRef.current();
                } catch (err) {
                    openErrorFromThrowable('pillar2GetObligationsErrorTitle', err);
                }
                return;
            }
            if (pendingAction === 'createTestOrg') {
                pendingPostAuthActionRef.current = 'submit';
                try {
                    await exchangeUkAuthCode(bff as Pillar2BffRequest, code, redirectUri ?? '');
                    await runCreateTestOrgRef.current();
                } catch (err) {
                    openErrorFromThrowable('pillar2CreateTestOrgErrorTitle', err);
                }
                return;
            }
            if (pendingAction === 'deleteTestOrg') {
                pendingPostAuthActionRef.current = 'submit';
                try {
                    await exchangeUkAuthCode(bff as Pillar2BffRequest, code, redirectUri ?? '');
                    await runDeleteTestOrgRef.current();
                } catch (err) {
                    openErrorFromThrowable('pillar2DeleteTestOrgErrorTitle', err);
                }
                return;
            }
            await runFiling(Pillar2AuthType.Oauth2, { authCode: code, redirectUri: redirectUri ?? '' });
        },
        onOAuthError: (err) => {
            showContentModal({
                title: t('pillar2UkAuthErrorTitle').toString(),
                message: err.errorDescription ?? err.error ?? t('pillar2UkAuthErrorDescription').toString(),
                kind: 'error',
            });
        },
    });

    const runFiling = useCallback(
        async (authType: Pillar2AuthType, oAuth2?: { authCode: string; redirectUri: string } | null, dac?: string | null) => {
            setIsFiling(true);
            const persistFilingPayloadToCloud = saveToCloudKey === 'yes';
            try {
                if (!pillar2FilingContext) {
                    throw new Error('Pillar 2 filing context is not available');
                }

                const response = await executePillar2Filing(
                    bff as Pillar2BffRequest,
                    filingMeta,
                    pillar2FilingContext,
                    persistFilingPayloadToCloud,
                    {
                        authType: authType,
                        oAuth2: oAuth2 ?? undefined,
                        dac: dac,
                    },
                    pillar2Settings,
                    pillar2DebugMode ? debugUploadedFile?.content : undefined
                );
                setFilingResultModal(response);
                if (response.isSuccess) {
                    setSubmitted(true);
                }
            } catch (err) {
                if (oAuth2 && isFetchUnauthorized(err)) {
                    showContentModal({
                        title: t('pillar2UkAuthSessionExpiredTitle').toString(),
                        message: t('pillar2UkAuthSessionExpiredDescription').toString(),
                        kind: 'warning',
                    });

                    try {
                        await authFlow.start();
                    } catch {
                        // start handles empty URL / popup; errors surfaced via modal where applicable
                    }
                } else {
                    openErrorFromThrowable('pillar2FilingErrorTitle', err);
                }
            } finally {
                setIsFiling(false);
            }
        },
        [
            bff,
            pillar2FilingContext,
            authFlow,
            filingMeta,
            openErrorFromThrowable,
            showContentModal,
            t,
            saveToCloudKey,
            pillar2Settings,
            pillar2DebugMode,
            debugUploadedFile,
        ]
    );

    const runGetObligations = useCallback(async () => {
        const jurisdiction = resolveJurisdiction(filingMeta.jurisdiction);
        const basePath = currentJurisdictionMetadata?.basePath ?? getJurisdictionBasePath(jurisdiction);

        setIsGettingObligations(true);
        try {
            const authenticated = await fetchPillar2AuthStatus(bff as Pillar2BffRequest, jurisdiction);
            if (!authenticated) {
                pendingPostAuthActionRef.current = 'getObligations';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort clear before OAuth
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            const [{ fromDate, toDate }, pillar2Id] = await Promise.all([
                getSamplesAccountingPeriod(bff as Pillar2BffRequest, filingMeta.jurisdiction),
                getPillar2Id(bff as Pillar2BffRequest, filingMeta.jurisdiction),
            ]);
            const response = await bff({
                url: `${basePath}/obligations`,
                method: 'get',
                params: { fromDate, toDate, pillar2Id },
            });
            showContentModal({
                title: t('pillar2GetObligationsResultTitle').toString(),
                message: t('pillar2GetObligationsResultDescription', { fromDate, toDate, pillar2Id }).toString(),
                body: response,
                kind: 'info',
            });
        } catch (err) {
            if (isFetchUnauthorized(err)) {
                pendingPostAuthActionRef.current = 'getObligations';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            openErrorFromThrowable('pillar2GetObligationsErrorTitle', err);
        } finally {
            setIsGettingObligations(false);
        }
    }, [authFlow, bff, currentJurisdictionMetadata?.basePath, filingMeta.jurisdiction, openErrorFromThrowable, showContentModal, t]);

    runGetObligationsRef.current = runGetObligations;

    const fetchPillar2Record = useCallback(async () => {
        if (!pillar2FilingContext) {
            throw new Error('Pillar 2 filing context is not available.');
        }

        const { enterpriseId, returnId } = pillar2FilingContext;

        return bff({
            url: `/Pillar2RecordStorage/${enterpriseId}/${returnId}`,
            method: 'get',
        });
    }, [bff, pillar2FilingContext]);

    const runFetchPillar2BlobContent = useCallback(
        async (contentType: 'submission' | 'response', modalTitle: string) => {
            if (!pillar2FilingContext) {
                showContentModal({
                    title: modalTitle,
                    message: 'Pillar 2 filing context is not available.',
                    kind: 'error',
                });
                return;
            }

            const { enterpriseId, returnId } = pillar2FilingContext;

            setIsFetchingPillar2Blob(true);
            try {
                const fileBlob = (await bff({
                    url: `/Pillar2ContentStorage/${contentType}/${enterpriseId}/${returnId}`,
                    method: 'get',
                    responseType: 'blob',
                })) as Blob;
                const blobText = await fileBlob.text();
                showContentModal({
                    title: modalTitle,
                    message: getFileNameFromBlobPath(`Pillar2Filings/${enterpriseId}_${returnId}.json`),
                    body: parseBlobTextForContentModal(blobText),
                    kind: 'info',
                });
            } catch (err) {
                openErrorFromThrowable('pillar2FilingErrorTitle', err);
            } finally {
                setIsFetchingPillar2Blob(false);
            }
        },
        [bff, openErrorFromThrowable, pillar2FilingContext, showContentModal]
    );

    const runFetchSubmissionBlob = useCallback(
        () => runFetchPillar2BlobContent('submission', 'Get Submission Blob File'),
        [runFetchPillar2BlobContent]
    );

    const runFetchResponseBlob = useCallback(
        () => runFetchPillar2BlobContent('response', 'Get Response Blob File'),
        [runFetchPillar2BlobContent]
    );

    const runFetchPillar2Record = useCallback(async () => {
        if (!pillar2FilingContext) {
            showContentModal({
                title: 'Get Database Record',
                message: 'Pillar 2 filing context is not available.',
                kind: 'error',
            });
            return;
        }

        setIsFetchingPillar2Record(true);
        try {
            const response = await fetchPillar2Record();
            showContentModal({
                title: 'Get Database Record',
                body: response,
                kind: 'info',
            });
        } catch (err) {
            openErrorFromThrowable('pillar2FilingErrorTitle', err);
        } finally {
            setIsFetchingPillar2Record(false);
        }
    }, [fetchPillar2Record, openErrorFromThrowable, pillar2FilingContext, showContentModal]);

    const runCreateTestOrg = useCallback(async () => {
        const jurisdiction = resolveJurisdiction(filingMeta.jurisdiction);
        const basePath = currentJurisdictionMetadata?.basePath ?? getJurisdictionBasePath(jurisdiction);

        setIsCreatingTestOrg(true);
        try {
            const authenticated = await fetchPillar2AuthStatus(bff as Pillar2BffRequest, jurisdiction);
            if (!authenticated) {
                pendingPostAuthActionRef.current = 'createTestOrg';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            const config = await getPillar2Config(bff as Pillar2BffRequest, filingMeta.jurisdiction);
            const pillar2Id = config.pillar2Id?.trim();
            const orgDetails = config.testOrganisation?.orgDetails;
            const accountingPeriod = config.testOrganisation?.accountingPeriod;
            if (
                !pillar2Id ||
                !orgDetails?.organisationName ||
                !orgDetails?.registrationDate ||
                !accountingPeriod?.startDate ||
                !accountingPeriod?.endDate
            ) {
                throw new Error(t('pillar2CreateTestOrgMissingDefaults').toString());
            }
            const body: TestOrganisationRequest = {
                orgDetails: {
                    organisationName: orgDetails.organisationName,
                    registrationDate: orgDetails.registrationDate,
                    domesticOnly: orgDetails.domesticOnly ?? true,
                },
                accountingPeriod: {
                    startDate: accountingPeriod.startDate,
                    endDate: accountingPeriod.endDate,
                },
            };
            const response = await bff({
                url: `${basePath}/setup/organisation`,
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                data: body,
                params: { pillar2Id },
            });
            showContentModal({
                title: t('pillar2CreateTestOrgSuccessTitle').toString(),
                message: t('pillar2CreateTestOrgSuccessDescription').toString(),
                body: response,
                kind: 'info',
            });
        } catch (err) {
            if (isFetchUnauthorized(err)) {
                pendingPostAuthActionRef.current = 'createTestOrg';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            openErrorFromThrowable('pillar2CreateTestOrgErrorTitle', err);
        } finally {
            setIsCreatingTestOrg(false);
        }
    }, [authFlow, bff, currentJurisdictionMetadata?.basePath, filingMeta.jurisdiction, openErrorFromThrowable, showContentModal, t]);

    runCreateTestOrgRef.current = runCreateTestOrg;

    const runDeleteTestOrg = useCallback(async () => {
        const jurisdiction = resolveJurisdiction(filingMeta.jurisdiction);
        const basePath = currentJurisdictionMetadata?.basePath ?? getJurisdictionBasePath(jurisdiction);

        setIsDeletingTestOrg(true);
        try {
            const authenticated = await fetchPillar2AuthStatus(bff as Pillar2BffRequest, jurisdiction);
            if (!authenticated) {
                pendingPostAuthActionRef.current = 'deleteTestOrg';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            const pillar2Id = await getPillar2Id(bff as Pillar2BffRequest, filingMeta.jurisdiction);
            await bff({
                url: `${basePath}/setup/organisation`,
                method: 'delete',
                params: {
                    pillar2Id,
                    enterpriseId: pillar2FilingContext?.enterpriseId,
                    returnId: pillar2FilingContext?.returnId,
                    returnKey: pillar2FilingContext?.returnKey,
                    columnIndex: pillar2FilingContext?.columnIndex,
                },
            });
            setSubmitted(false);
            showToast(t('pillar2DeleteTestOrgSuccessTitle').toString(), t('pillar2DeleteTestOrgSuccessDescription').toString(), 'success');
        } catch (err) {
            if (isFetchUnauthorized(err)) {
                pendingPostAuthActionRef.current = 'deleteTestOrg';
                try {
                    await postPillar2AuthLogout(bff as Pillar2BffRequest, jurisdiction);
                } catch {
                    // best-effort
                }
                try {
                    await authFlow.start();
                } catch {
                    // start handles empty URL / popup
                }
                return;
            }
            openErrorFromThrowable('pillar2DeleteTestOrgErrorTitle', err);
        } finally {
            setIsDeletingTestOrg(false);
        }
    }, [
        authFlow,
        bff,
        currentJurisdictionMetadata?.basePath,
        filingMeta.jurisdiction,
        openErrorFromThrowable,
        pillar2FilingContext,
        setSubmitted,
        showToast,
        t,
    ]);

    runDeleteTestOrgRef.current = runDeleteTestOrg;

    const handlePreviewOutput = useCallback(async () => {
        setPreviewOpen(true);
        setPreviewStatus('loading');
        setPreviewContent(undefined);
        setPreviewErrorDetails(undefined);
        try {
            const raw = await getEntityEfileData({ reportId, columnIndex: 0, enterpriseKey: enterprise?.enterpriseKey });
            setPreviewContent(formatPreviewContent(raw));
            setPreviewStatus('success');
        } catch (err) {
            setPreviewErrorDetails(extractFilingErrorResponseBody(err) ?? err);
            setPreviewStatus('error');
        }
    }, [enterprise?.enterpriseKey, getEntityEfileData, reportId]);

    const cancelConfirmedSubmit = useCallback(() => {
        flushSync(() => {
            setPendingConfirm(null);
        });
    }, []);

    const executeConfirmedSubmit = useCallback(async () => {
        if (!pendingConfirm) {
            return;
        }

        const jurisdiction = resolveJurisdiction(filingMeta.jurisdiction);
        const authType: Pillar2AuthType = pendingConfirm.authType;

        flushSync(() => {
            setPendingConfirm(null);
        });

        if (authType === Pillar2AuthType.Oauth2) {
            setIsSubmittingAuth(true);

            try {
                const authenticated = await fetchPillar2AuthStatus(bff as Pillar2BffRequest, jurisdiction);

                if (authenticated) {
                    await runFiling(authType);
                } else {
                    try {
                        await authFlow.start();
                    } catch {
                        // start handles empty URL / popup; errors surfaced via modal where applicable
                    }
                }
            } catch {
                showContentModal({
                    title: t('pillar2FilingErrorTitle').toString(),
                    message: t('pillar2UkAuthStatusErrorDescription').toString(),
                    kind: 'error',
                });
            } finally {
                setIsSubmittingAuth(false);
            }
        }

        if (authType === Pillar2AuthType.Dac) {
            setShowDacModal(true);
        }

        if (authType === Pillar2AuthType.None) {
            await runFiling(authType);
        }
    }, [
        authFlow,
        bff,
        filingMeta.jurisdiction,
        openErrorFromThrowable,
        pendingConfirm,
        pillar2FilingContext,
        runFiling,
        showContentModal,
        showToast,
        t,
    ]);

    const handleSubmit = useCallback(() => {
        const authType: Pillar2AuthType = currentJurisdictionMetadata?.authType ?? Pillar2AuthType.None;
        setPendingConfirm({ authType, alreadySubmitted: submitted });
    }, [currentJurisdictionMetadata?.authType, submitted]);

    // const continueWithDac = useCallback(() => {});

    useEffect(() => {
        setSaveToCloudKey('');
    }, [reportId]);

    useEffect(() => {
        if (!isOpen) {
            setSaveToCloudKey('');
            setShowDacModal(false);
            setDac(null);
            setPreviewOpen(false);
            setPreviewStatus('loading');
            setPreviewContent(undefined);
            setIsSubmittingAuth(false);
            setIsFiling(false);
            setIsGettingObligations(false);
            setIsCreatingTestOrg(false);
            setIsDeletingTestOrg(false);
            pendingPostAuthActionRef.current = 'submit';
            setPendingConfirm(null);
            setPreviewOpen(false);
            clearContentModalTimer();
            setContentModal(null);
            authFlow.cleanup();
        }
    }, [authFlow, clearContentModalTimer, isOpen]);

    useEffect(() => {
        return () => {
            clearContentModalTimer();
            authFlow.cleanup();
        };
    }, [authFlow, clearContentModalTimer]);

    useEffect(() => {
        if (showDacModal) {
            requestAnimationFrame(() => dacInputRef.current?.focus());
        }
    }, [showDacModal]);

    const submitConfirmContent =
        pendingConfirm === null
            ? ''
            : `${pendingConfirm.alreadySubmitted ? `${t('pillar2ResubmitConfirmPrefix').toString()} ` : ''}${t(
                  `pillar2SubmitConfirmDescription${pendingConfirm.authType}`
              ).toString()}`;

    const anyDebugBusy =
        isSubmittingAuth ||
        isFiling ||
        isGettingObligations ||
        isCreatingTestOrg ||
        isDeletingTestOrg ||
        isFetchingPillar2Blob ||
        isFetchingPillar2Record ||
        pendingConfirm !== null;

    const isBlockingOverlayOpen = contentModal !== null || pendingConfirm !== null || showDacModal || previewOpen;

    const handleDrawerOpenChange = useCallback(
        (_: unknown, data: { open: boolean }) => {
            if (!data.open && !isBlockingOverlayOpen) {
                onClose();
            }
        },
        [isBlockingOverlayOpen, onClose]
    );

    const dismissDacModal = () => {
        setShowDacModal(false);
        setDac(null);
    };

    const continueFromDacModal = useCallback(async () => {
        const enteredDac: string | null = dac;

        dismissDacModal();
        await runFiling(Pillar2AuthType.Dac, null, enteredDac);
    }, [dac]);

    if (!isPillar2Enabled && isOpen) {
        return null;
    }

    return (
        <>
            <OverlayDrawer
                data-testid='pillar2PanelDrawer'
                open={isOpen}
                onOpenChange={handleDrawerOpenChange}
                position='end'
                modalType='modal'
                className={panelStyles.pillar2OverlayDrawer}
            >
                <DrawerHeader className={panelStyles.drawerHeader}>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance='subtle'
                                icon={<DismissRegular />}
                                aria-label={t('closePanel').toString()}
                                onClick={onClose}
                            />
                        }
                    >
                        {t('pillar2ReturnFilingReview')}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody className={panelStyles.drawerBody}>
                    <div className={panelStyles.fieldsStack}>
                        {pillar2Settings!.efileReturnMetadata &&
                            Object.entries(pillar2Settings!.efileReturnMetadata).map((entry) => (
                                <div key={entry[0]} className={panelStyles.fieldRow}>
                                    <span className={panelStyles.fieldLabel}>{entry[1].description}</span>
                                    <span className={panelStyles.fieldValue}>{formatPillar2SettingValue(entry[1].value)}</span>
                                </div>
                            ))}
                    </div>
                </DrawerBody>

                <DrawerFooter className={panelStyles.footerSection}>
                    {showSubmitUnavailableMessage && (
                        <div
                            className={panelStyles.submitUnavailableMessageContainer}
                            role='note'
                            tabIndex={0}
                            aria-labelledby='pillar2-submit-unavailable-message'
                        >
                            <Warning20Regular className={panelStyles.submitUnavailableMessageIcon} aria-hidden='true' />
                            <span id='pillar2-submit-unavailable-message'>
                                {t(getSubmitUnavailableMessageKey(taxReturnReport?.pillar2Settings?.efileFormat))}
                            </span>
                        </div>
                    )}
                    {isSubmitButtonAvailable && (
                        <div className={panelStyles.saveToCloudCard}>
                            <div className={panelStyles.saveToCloudBlock}>
                                <span className={panelStyles.saveToCloudLabel} id='pillar2-save-to-cloud-label'>
                                    <span className={panelStyles.requiredAsterisk} aria-hidden='true'>
                                        *
                                    </span>{' '}
                                    {t('pillar2SaveFilingDataInCorptax')}
                                </span>
                                <RadioGroup
                                    layout='horizontal'
                                    className={panelStyles.saveToCloudRadioGroup}
                                    aria-labelledby='pillar2-save-to-cloud-label'
                                    aria-required={true}
                                    value={saveToCloudKey}
                                    onChange={(_, data) => setSaveToCloudKey(data.value)}
                                >
                                    <Radio value='yes' label={t('pillar2SaveToCloudOptionYes')} />
                                    <Radio value='no' label={t('pillar2SaveToCloudOptionNo')} />
                                </RadioGroup>
                            </div>
                        </div>
                    )}
                    <div className={panelStyles.actionFooter}>
                        {currentJurisdictionMetadata?.supportedOperations.map((op) => {
                            if (!pillar2DebugMode) return null;
                            return (
                                <Button
                                    appearance='secondary'
                                    key={op.id}
                                    icon={
                                        op.id === 'createTestOrg' ? (
                                            <AddRegular />
                                        ) : op.id === 'deleteTestOrg' ? (
                                            <DeleteRegular />
                                        ) : undefined
                                    }
                                    onClick={() => {
                                        if (op.id === 'getObligations') void runGetObligations();
                                        else if (op.id === 'createTestOrg') void runCreateTestOrg();
                                        else if (op.id === 'deleteTestOrg') void runDeleteTestOrg();
                                        else if (op.id === 'getSubmissionBlobData') void runFetchSubmissionBlob();
                                        else if (op.id === 'getResponseBlobData') void runFetchResponseBlob();
                                        else if (op.id === 'getCosmosData') void runFetchPillar2Record();
                                    }}
                                    disabled={anyDebugBusy}
                                    aria-label={op.displayName}
                                >
                                    {op.displayName}
                                </Button>
                            );
                        })}
                    </div>
                    {pillar2DebugMode && (
                        <div className={panelStyles.actionFooter}>
                            <input
                                ref={debugFileInputRef}
                                type='file'
                                accept='.xml,.json'
                                className={panelStyles.hiddenFileInput}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    // Allow re-selecting the same file later.
                                    e.target.value = '';
                                    if (!file) {
                                        return;
                                    }
                                    void file.text().then((content) => setDebugUploadedFile({ name: file.name, content }));
                                }}
                            />
                            <Button
                                appearance='secondary'
                                onClick={() => debugFileInputRef.current?.click()}
                                disabled={anyDebugBusy}
                                aria-label='Upload File'
                            >
                                Upload File
                            </Button>
                            {debugUploadedFile && (
                                <>
                                    <span className={panelStyles.debugFileName}>Using: {debugUploadedFile.name}</span>
                                    <Button
                                        appearance='subtle'
                                        icon={<DeleteRegular />}
                                        onClick={() => setDebugUploadedFile(null)}
                                        disabled={anyDebugBusy}
                                        aria-label='Clear uploaded file'
                                    />
                                </>
                            )}
                        </div>
                    )}
                    <div className={panelStyles.actionFooter}>
                        <Button
                            appearance='secondary'
                            onClick={() => {
                                void handlePreviewOutput();
                            }}
                            disabled={anyDebugBusy}
                            aria-label='Preview Output'
                        >
                            Preview Output
                        </Button>

                        {isSubmitButtonAvailable && (
                            <Button
                                appearance='primary'
                                icon={submitted ? <CheckmarkRegular /> : undefined}
                                onClick={handleSubmit}
                                disabled={anyDebugBusy || !saveToCloudKey}
                                aria-label={submitted ? t('pillar2SubmittedButton').toString() : t('pillar2Submitbutton').toString()}
                            >
                                {submitted ? t('pillar2SubmittedButton').toString() : t('pillar2Submitbutton').toString()}
                            </Button>
                        )}
                    </div>
                </DrawerFooter>
            </OverlayDrawer>
            {pendingConfirm !== null && (
                <ConfirmationDialogV9
                    title={t('pillar2SubmitConfirmTitle').toString()}
                    content={submitConfirmContent}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    onClick={cancelConfirmedSubmit}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                    aria-label={t('cancel').toString()}
                                >
                                    {t('cancel')}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                onClick={executeConfirmedSubmit}
                                data-analytics-button-name='confirmation_dialog_save'
                                aria-label={t('confirm').toString()}
                            >
                                {t('confirm')}
                            </Button>
                        </>
                    }
                />
            )}
            <Pillar2FilingResultModal
                isOpen={filingResultModal !== null}
                response={filingResultModal}
                returnName={taxReturnReport?.name}
                onClose={() => setFilingResultModal(null)}
            />
            <ModalV9
                isOpen={contentModal !== null}
                onClose={dismissContentModal}
                isBlocking
                showModalCloseButton={false}
                modalMaxWidthInPixels={760}
                modalMaxHeightInPixels={640}
                zIndex={1000001}
                headerContent={contentModal?.title}
                bodyContent={
                    contentModal ? (
                        <>
                            {contentModal.message ? <p className={panelStyles.contentModalMessage}>{contentModal.message}</p> : null}
                            {contentModalBodyJson !== null ? (
                                <>
                                    <div className={panelStyles.contentModalDetailsLabel}>{t('pillar2ContentModalDetailsLabel')}</div>
                                    <pre className={panelStyles.contentModalPre}>{contentModalBodyJson}</pre>
                                </>
                            ) : null}
                        </>
                    ) : null
                }
                buttons={
                    <div className={panelStyles.modalActions}>
                        <Button
                            appearance='secondary'
                            icon={<CopyRegular />}
                            onClick={() => {
                                void handleCopyContentModal();
                            }}
                            disabled={!contentModal?.message && contentModalBodyJson === null}
                            aria-label={t('pillar2ModalCopyButton').toString()}
                        >
                            {t('pillar2ModalCopyButton').toString()}
                        </Button>
                        <Button appearance='secondary' onClick={dismissContentModal} aria-label={t('ok').toString()}>
                            {t('ok').toString()}
                        </Button>
                    </div>
                }
            />
            <ModalV9
                isOpen={showDacModal}
                onClose={dismissDacModal}
                isBlocking={true}
                showModalCloseButton={true}
                modalMaxWidthInPixels={500}
                modalMaxHeightInPixels={800}
                zIndex={1000001}
                headerContent={t('enterDigitalAccessCodeTitle').toString()}
                bodyContent={
                    <>
                        <p>{t('enterDigitalAccessCodeDescription').toString()}</p>
                        <br></br>
                        <Input
                            className={panelStyles.dacInput}
                            value={dac ?? ''}
                            onChange={(_, data) => setDac(data.value.replace(/\D/g, ''))}
                            input={{ ref: dacInputRef }}
                            inputMode='numeric'
                            aria-label='DAC'
                        />
                    </>
                }
                buttons={
                    <Button
                        appearance='primary'
                        onClick={continueFromDacModal}
                        disabled={!dac?.trim()}
                        aria-label={t('continue').toString()}
                    >
                        {t('continue').toString()}
                    </Button>
                }
            />
            <Pillar2DataPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                status={previewStatus}
                content={previewContent}
                contentType={taxReturnReport?.pillar2Settings?.efileFormat}
                taxReturnName={taxReturnReport?.name}
                errorDetails={previewErrorDetails}
            />
        </>
    );
};

export default Pillar2Panel;
